import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import { getSiteConfig } from "@/config/siteConfigs";
import styles from "./Form.module.scss";

const PartnerForm: React.FC = () => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();

  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    postalCode: "",
    city: "",
    phone: "",
    contactName: "",
    companyType: "",
    services: {
      diagnosis: false,
      partsExchange: false,
      repairs: false,
      maintenance: false,
      others: false,
    },
    message: "",
    consent: false,
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox" && "checked" in e.target) {
      const { checked } = e.target as HTMLInputElement;
      if (name in formData.services) {
        setFormData((prevData) => ({
          ...prevData,
          services: {
            ...prevData.services,
            [name]: checked,
          },
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: checked,
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaToken) {
      alert(t("partnerForm.errors.recaptcha"));
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackpartners",
      legid: 1,
      language: siteConfig.language,
      data: {
        companyName: formData.companyName,
        email: formData.email,
        postalCode: formData.postalCode,
        city: formData.city,
        phone: formData.phone,
        contactName: formData.contactName,
        companyType: formData.companyType,
        services: formData.services,
        message: formData.message,
        recaptchaToken: captchaToken,
      },
    };

    try {
      setLoading(true);
      const response = await sendToCRM(formPayload);
      console.log("CRM Response:", response);
      alert(t("partnerForm.success"));

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "form_submit",
          form_name: "partner_form",
          form_type: "feedbackpartners",
        });
      }

      setFormData({
        companyName: "",
        email: "",
        postalCode: "",
        city: "",
        phone: "",
        contactName: "",
        companyType: "",
        services: {
          diagnosis: false,
          partsExchange: false,
          repairs: false,
          maintenance: false,
          others: false,
        },
        message: "",
        consent: false,
      });

      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      console.error("Fehler beim Senden der Anfrage", error);
      alert(t("partnerForm.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: "1",
        language: siteConfig.language,
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>{t("partnerForm.heading")} </h3>
            <p className={styles.subheading}>{t("partnerForm.subheading")}</p>
          </div>

          <form className={styles.feedbackForm} onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="companyName" className={styles.label}>
                  {t("partnerForm.fields.companyName.label")} *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  placeholder={`${t(
                    "partnerForm.fields.companyName.placeholder"
                  )} *`}
                  value={formData.companyName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="contactName" className={styles.label}>
                  {t("partnerForm.fields.contactName.label")} *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  placeholder={`${t(
                    "partnerForm.fields.contactName.placeholder"
                  )} *`}
                  value={formData.contactName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  {t("partnerForm.fields.email.label")} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={`${t("partnerForm.fields.email.placeholder")} *`}
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                  {t("partnerForm.fields.phone.label")} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={`${t("partnerForm.fields.phone.placeholder")} *`}
                  value={formData.phone}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={`${styles.formGroup} ${styles.w32}`}>
                <label htmlFor="postalCode" className={styles.label}>
                  {t("partnerForm.fields.postalCode.label")} *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  placeholder={`${t(
                    "partnerForm.fields.postalCode.placeholder"
                  )} *`}
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w64}`}>
                <label htmlFor="city" className={styles.label}>
                  {t("partnerForm.fields.city.label")} *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  placeholder={`${t("partnerForm.fields.city.placeholder")} *`}
                  value={formData.city}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label htmlFor="companyType" className={styles.label}>
                {t("partnerForm.fields.companyType.label")} *
              </label>
              <select
                id="companyType"
                name="companyType"
                value={formData.companyType}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">
                  {t("partnerForm.fields.companyType.placeholder")}
                </option>
                <option value="company1">
                  {t("partnerForm.fields.companyTypes.workshop")}
                </option>
                <option value="company2">
                  {t("partnerForm.fields.companyTypes.dealership")}
                </option>
                <option value="company3">
                  {t("partnerForm.fields.companyTypes.fleetManagement")}
                </option>
                <option value="company4">
                  {t("partnerForm.fields.companyTypes.other")}
                </option>
              </select>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label className={styles.label}>
                {t("partnerForm.fields.services.label")}
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="diagnosis"
                    checked={formData.services.diagnosis}
                    onChange={handleChange}
                  />
                  {t("partnerForm.fields.services.diagnosis")}
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="repairs"
                    checked={formData.services.repairs}
                    onChange={handleChange}
                  />
                  {t("partnerForm.fields.services.repairs")}
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="partsExchange"
                    checked={formData.services.partsExchange}
                    onChange={handleChange}
                  />
                  {t("partnerForm.fields.services.partsExchange")}
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="maintenance"
                    checked={formData.services.maintenance}
                    onChange={handleChange}
                  />
                  {t("partnerForm.fields.services.maintenance")}
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="others"
                    checked={formData.services.others}
                    onChange={handleChange}
                  />
                  {t("partnerForm.fields.services.others")}
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                {t("partnerForm.fields.message.label")} *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder={`${t("partnerForm.fields.message.placeholder")} *`}
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              />
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">
                {t("partnerForm.consent")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  {t("partnerForm.consentLink")}
                </a>
                .
              </label>
            </div>

            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteConfig.recaptcha.siteKey}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonMd ${styles.submitButton}`}
              disabled={loading || !formData.consent || !captchaToken}
            >
              {loading ? t("partnerForm.submitting") : t("partnerForm.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerForm;
